import {immerable} from "immer";
import ExtractionDataDto from "@/models/interfaces/dtos/extraction/extraction-data-dto.ts";
import ProjectMeasures from "@/pages/takeoff/context/project-measure/models/project-measures.ts";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import ExtractionNodeElement from "./interfaces/extraction-node-element.ts";
import ExtractionSheetLeafNode from "./extraction-sheet-leaf-node.ts";
import ExtractionSheetArticleNode from "./extraction-sheet-article-node.ts";
import ProjectIndicatorsDto from "@/models/interfaces/dtos/indicators/project-indicators-dto.ts";
import ExtractionSheetArticleElementBase
    from "@/pages/takeoff/layouts/extraction/context/models/article-elements/extraction-sheet-article-element-base.ts";

class ExtractionSheetTree {
    [immerable] = true;
    
    id: string;
    
    name: string
    
    children: ExtractionSheetLeafNode[];
    
    constructor(name: string) {
        this.id = crypto.randomUUID();
        this.name = name;
        this.children = [];
    }

    static fromDto(dto: ExtractionDataDto, projectMeasures: ProjectMeasures, measureTypes: MeasureType[], projectIndicatorsData?: ProjectIndicatorsDto): ExtractionSheetTree {
        const sheet = new ExtractionSheetTree(dto.name);
        sheet.children = dto.children?.map(nodeDto => ExtractionSheetLeafNode.fromDto(nodeDto, projectMeasures, measureTypes, projectIndicatorsData)) ?? [];
        return sheet;
    }

    updateName(name: string) {
        this.name = name;
    }

    /* NOEUDS */
    
    addNode(nodeName: string) {
        const node = new ExtractionSheetLeafNode(nodeName);
        this.children.push(node);
    }
    
    getNodeById(nodeId: string): ExtractionSheetLeafNode | undefined {
        const found = this.findElementById(nodeId);
        if (found instanceof ExtractionSheetLeafNode) {
            return found;
        }
        return undefined;
    }
    
    removeNode(nodeId: string) {
        this.children = this.children.filter(n => n.id !== nodeId);
        this.children.forEach(node => node.removeChildrenNode(nodeId));
    }
    
    /* ARTICLES */
    
    getArticleById(articleId: string): ExtractionSheetArticleNode | undefined {
        const found = this.findElementById(articleId);
        if (found instanceof ExtractionSheetArticleNode) {
            return found;
        }
        return undefined;
    }
    
    removeArticle(articleId: string) {
        this.children.forEach(node => node.removeArticle(articleId));
    }

    getArticleElementById(id: string): ExtractionSheetArticleElementBase | undefined {
        const found = this.findElementById(id);
        if (found instanceof ExtractionSheetArticleElementBase) {
            return found;
        }
        return undefined;
    }

    /* Helpers */
    
    findElementById(nodeId: string): ExtractionNodeElement | undefined {
        for (let i = 0; i < this.children.length; i += 1) {
            const node = this.children[i];
            const found = node.findElementById(nodeId);
            if (found) {
                return found;
            }
        }
        return undefined;
    }

    getHierarchy(targetId: string): string[] | undefined {
        for (let i = 0; i < this.children.length; i += 1) {
            const node = this.children[i];
            const path = node.getHierarchy(targetId);
            if (path) {
                return [this.name, ...path];
            }
        }
        return undefined;
    }
    
    getNodeArticlesElements(): ExtractionSheetArticleElementBase[] {
        const elements: ExtractionSheetArticleElementBase[] = [];
        this.children.forEach(node => {
            elements.push(...node.getNodeArticlesElements());
        });
        return elements;
    }
}

export default ExtractionSheetTree;